// Вкладка «Доступ»: всё про вход ЭТОГО человека, в его же карточке.
//
// ## Зачем она появилась
//
// Заказчик 14.09.2026 про раздачу доступов: «концепция замудрённая, я там теряюсь». Терялся он
// не на словах, а на дороге: человек живёт в реестре «Людей», а его вход — в «Настройках», в
// общем списке учётных записей рядом с двумя десятками чужих. Чтобы пустить одного человека,
// надо было уйти из его карточки, найти его в другом списке по второму имени (имя входа), и
// там нажать три кнопки подряд. Правило 3 («действие живёт там, где объект») нарушалось
// ровно в том месте, где оно дороже всего: доступ — это про человека.
//
// Здесь всё про одного человека и сразу: роли, вход в программу, ссылка для входа с кодом,
// когда она годна, когда он заходил в последний раз. Настройки при этом никуда не делись:
// там список «кто вообще входит в систему», и это другой вопрос.
//
// ## Два заслона, и оба объясняются словами
//
// Роль СТАРШЕ своей не выдаётся: иначе тот, кому доверили раздачу доступов, выпишет себе
// руководство вместе со списанием долгов. Решает это сервер и присылает готовый ответ полем
// `assignable` — своей копии лестницы старшинства здесь нет намеренно.
//
// Чужую ФРАЗУ для входа задаёт только администратор: поставивший чужую фразу войдёт под этим
// человеком. Поэтому у остальных этой кнопки нет вовсе, а вместо неё написано, что ссылка для
// входа в приложение фразы не требует.

import { useState } from 'react';

import { RiKey2Line, RiLinkM, RiSmartphoneLine } from '@remixicon/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { EmployeeOut } from '@/shared/api/endpoints/people';
import type { RoleOut, UserOut } from '@/shared/api/endpoints/users';
import { patchUser, revokeInvite } from '@/shared/api/endpoints/users';
import { useAuthStore } from '@/shared/auth/store';
import { can } from '@/shared/rbac/permissions';
import { formatDateDMY, formatDateOnly, formatTimeHM, parseMoment } from '@/shared/time/format';
import * as Button from '@/shared/ui/button';
import { CheckboxField } from '@/shared/ui/checkbox-field';
import { КнопкаКопирования } from '@/shared/ui/copy-button';
import { ErrorAlert } from '@/shared/ui/error-alert';
import { FieldList, FieldRow } from '@/shared/ui/field-row';
import * as Hint from '@/shared/ui/hint';
import { ScreenState, Skeleton } from '@/shared/ui/screen-state';
import { TextField } from '@/shared/ui/text-field';
import { ключиУчётных, useРоли, useУчётныеЗаписи, учётнаяЗаписьЧеловека } from '@/shared/users/queries';

import { выдатьДоступ, type ИтогВыдачи } from '../access/выдача';

import { PhoneBlock } from './PhoneBlock';

/** Право заводить учётные записи и менять их данные. */
const ПРАВО_МЕНЯТЬ = 'core.user.manage';
/** Право открыть или закрыть человеку вход. Есть у руководства и операционного руководства. */
const ПРАВО_ДОСТУПА = 'core.user.access';
/** Право выдать и погасить ссылку с кодом. */
const ПРАВО_КОДОВ = 'core.user.invite';
/** Право менять роли. У операционного руководства его нет. */
const ПРАВО_РОЛЕЙ = 'core.role.assign';
/** Право убрать учётную запись. С 13.09.2026 только у администратора. */
const ПРАВО_УДАЛЯТЬ = 'core.user.delete';

/**
 * Роль администратора.
 *
 * Проверяется РОЛЬ, а не право, и ровно в одном месте — у смены чужой фразы для входа. Так же
 * устроен и заслон на сервере: право можно выдать кому угодно настройкой, а смысл заслона в
 * том, что таких людей ровно один круг (`server/app/services/users.py`).
 */
const АДМИНИСТРАТОР = 'admin';

/**
 * Пустой список ролей — ОДИН на весь модуль, а не новый на каждый кадр.
 *
 * `состояние.user?.roles ?? []` выглядит безобидно и роняет экран: хранилище сравнивает
 * прежнее значение с новым по ссылке, а свежесозданный пустой массив не равен предыдущему
 * никогда. React считает, что состояние изменилось, перерисовывает — и так до «Maximum update
 * depth exceeded». Поймано прогоном вкладки, а не глазами: на экране это выглядит как
 * подвисший кабинет, а в коде как обычная подстановка умолчания.
 */
const БЕЗ_РОЛЕЙ: readonly string[] = [];

/** Момент словами: «14.09.2026 в 09:12». Для «последний вход» одной даты мало. */
function когда(значение: string | null | undefined): string | null {
  const момент = parseMoment(значение);
  return момент === null ? null : `${formatDateDMY(момент)} в ${formatTimeHM(момент)}`;
}

export function AccessTab({ employee }: { employee: EmployeeOut }) {
  const права = useAuthStore((состояние) => состояние.permissions);
  const мойИдент = useAuthStore((состояние) => состояние.user?.id ?? null);
  const мойРоли = useAuthStore((состояние) => состояние.user?.roles ?? БЕЗ_РОЛЕЙ);
  const можноМенять = can(права, ПРАВО_МЕНЯТЬ);
  const можноДоступ = can(права, ПРАВО_ДОСТУПА);
  const можноКоды = can(права, ПРАВО_КОДОВ);
  const можноРоли = can(права, ПРАВО_РОЛЕЙ);
  const можноУдалять = can(права, ПРАВО_УДАЛЯТЬ);
  const яАдминистратор = мойРоли.includes(АДМИНИСТРАТОР);

  const клиент = useQueryClient();
  const пользователи = useУчётныеЗаписи();
  const роли = useРоли();
  const запись = учётнаяЗаписьЧеловека(пользователи.data, employee.id);

  const обновить = () => клиент.invalidateQueries({ queryKey: ключиУчётных.все });

  return (
    <div className='flex flex-col gap-4 p-5'>
      <ScreenState
        data={пользователи.data}
        error={пользователи.error}
        subject='учётные записи'
        // Пустой список учётных записей — не пустое состояние ЭТОГО экрана: вопрос здесь про
        // одного человека, и ответ «записи у него нет» рисуется ниже своими словами.
        isEmpty={() => false}
        onRetry={() => void пользователи.refetch()}
        loading={
          // Скелет, а не строка «учётной записи нет». Пока список едет, ответа мы не знаем, а
          // написанное «записи нет» человек читает как ответ — и заводит вторую запись тому,
          // у кого она уже есть.
          <div className='flex flex-col gap-3'>
            <Skeleton className='h-5 w-1/3' />
            <Skeleton className='h-24 w-full' />
          </div>
        }
        empty={null}
      >
        {() =>
          запись === null || запись === undefined ? (
            <БезЗаписи
              employee={employee}
              роли={роли.data ?? []}
              ролиЕдут={роли.isPending}
              можноМенять={можноМенять}
              можноРоли={можноРоли}
              можноКоды={можноКоды}
              обновить={обновить}
            />
          ) : (
            <СЗаписью
              employee={employee}
              запись={запись}
              роли={роли.data ?? []}
              ролиЕдут={роли.isPending}
              мойИдент={мойИдент}
              можноМенять={можноМенять}
              можноДоступ={можноДоступ}
              можноКоды={можноКоды}
              можноРоли={можноРоли}
              можноУдалять={можноУдалять}
              яАдминистратор={яАдминистратор}
              обновить={обновить}
            />
          )
        }
      </ScreenState>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════════════════
// Человека в системе ещё нет
// ══════════════════════════════════════════════════════════════════════════════════════════

function БезЗаписи({
  employee,
  роли,
  ролиЕдут,
  можноМенять,
  можноРоли,
  можноКоды,
  обновить,
}: {
  employee: EmployeeOut;
  роли: RoleOut[];
  ролиЕдут: boolean;
  можноМенять: boolean;
  можноРоли: boolean;
  можноКоды: boolean;
  обновить: () => void;
}) {
  const [открыт, setОткрыт] = useState(false);

  return (
    <div className='flex flex-col gap-3'>
      <div className='flex flex-col gap-1'>
        <span className='text-label-sm text-text-strong-950'>В систему не входит</span>
        <span className='max-w-prose text-paragraph-sm text-text-sub-600'>
          Учётной записи у человека нет: ни своего расчётного листа в телефоне, ни входа в
          программу. Пригласите его — выберите роль и пришлите ссылку.
        </span>
      </div>

      {!открыт && (
        <div>
          <Button.Root
            type='button'
            variant='primary'
            mode='filled'
            size='xsmall'
            onClick={() => setОткрыт(true)}
          >
            <Button.Icon as={RiLinkM} />
            Пригласить
          </Button.Root>
        </div>
      )}

      {открыт && (
        <МастерВыдачи
          employee={employee}
          запись={null}
          роли={роли}
          ролиЕдут={ролиЕдут}
          можноМенять={можноМенять}
          можноРоли={можноРоли}
          можноКоды={можноКоды}
          обновить={обновить}
          отмена={() => setОткрыт(false)}
        />
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════════════════
// Учётная запись есть
// ══════════════════════════════════════════════════════════════════════════════════════════

function СЗаписью({
  employee,
  запись,
  роли,
  ролиЕдут,
  мойИдент,
  можноМенять,
  можноДоступ,
  можноКоды,
  можноРоли,
  можноУдалять,
  яАдминистратор,
  обновить,
}: {
  employee: EmployeeOut;
  запись: UserOut;
  роли: RoleOut[];
  ролиЕдут: boolean;
  мойИдент: string | null;
  можноМенять: boolean;
  можноДоступ: boolean;
  можноКоды: boolean;
  можноРоли: boolean;
  можноУдалять: boolean;
  яАдминистратор: boolean;
  обновить: () => void;
}) {
  const [выдаём, setВыдаём] = useState(false);
  const [правимФразу, setПравимФразу] = useState(false);
  const [новаяФраза, setНоваяФраза] = useState('');

  const названияРолей = роли
    .filter((роль) => запись.roles.includes(роль.code))
    .map((роль) => роль.title)
    .join(', ');

  const входВПрограмму = useMutation({
    mutationFn: (впускать: boolean) => patchUser(запись.id, { cabinet_access: впускать }),
    onSuccess: обновить,
  });
  const отзыв = useMutation({
    mutationFn: () => revokeInvite(запись.id),
    onSuccess: обновить,
  });
  const фраза = useMutation({
    mutationFn: (значение: string) => patchUser(запись.id, { password: значение }),
    onSuccess: () => {
      setПравимФразу(false);
      setНоваяФраза('');
      обновить();
    },
  });

  return (
    <div className='flex flex-col gap-4'>
      <FieldList title='Как этот человек входит'>
        <FieldRow
          label='Имя входа'
          value={запись.login}
          note='его набирают только в программе на компьютере; в телефон входят по ссылке'
        />
        <FieldRow
          label='Роли'
          value={названияРолей === '' ? null : названияРолей}
          pending={ролиЕдут}
          missing='ролей нет'
          missingNote='человек войдёт и не увидит ничего: ни своего расчётного листа, ни денег'
        />
        <FieldRow
          label='Вход в программу на компьютере'
          value={запись.cabinet_access ? 'открыт' : 'закрыт'}
          note={
            запись.cabinet_access
              ? 'человек может открыть программу под своим именем и фразой'
              : 'в телефон он при этом входит: приложению вход в программу не нужен'
          }
        />
        <FieldRow
          label='Фраза для входа в программу'
          value={запись.has_password ? 'задана' : null}
          missing='не задана'
          missingNote='в программу на компьютере он не войдёт; в приложении фраза не нужна'
        />
        <FieldRow
          label='Ссылка для входа'
          value={
            запись.invite_live
              ? `выдана, годна до ${formatDateOnly(запись.invite_expires_at)}`
              : null
          }
          missing={запись.last_login_at === null ? 'не выдана' : 'не нужна: человек уже входил'}
          missingNote={
            запись.last_login_at === null
              ? 'без неё человек не войдёт в приложение на телефоне'
              : undefined
          }
          note={
            запись.invite_live
              ? 'человек ею ещё не воспользовался. Саму ссылку показать второй раз нельзя — выдайте новую'
              : undefined
          }
        />
        <FieldRow
          label='Последний вход'
          value={когда(запись.last_login_at)}
          missing='ещё ни разу не входил'
        />
      </FieldList>

      {/* Первичное действие ровно одно — дать человеку войти. Остальное рядом обводкой. */}
      <div className='flex flex-wrap items-center gap-2'>
        {можноКоды && !выдаём && (
          <Button.Root
            type='button'
            variant='primary'
            mode='filled'
            size='xsmall'
            onClick={() => setВыдаём(true)}
          >
            <Button.Icon as={RiSmartphoneLine} />
            {запись.invite_live ? 'Выдать новую ссылку' : 'Выдать ссылку для входа'}
          </Button.Root>
        )}
        {можноКоды && запись.invite_live && (
          <Button.Root
            type='button'
            variant='neutral'
            mode='stroke'
            size='xsmall'
            onClick={() => отзыв.mutate()}
          >
            {отзыв.isPending ? 'Гасим…' : 'Погасить ссылку'}
          </Button.Root>
        )}
        {можноДоступ && запись.id !== мойИдент && (
          <Button.Root
            type='button'
            variant='neutral'
            mode='stroke'
            size='xsmall'
            onClick={() => входВПрограмму.mutate(!запись.cabinet_access)}
          >
            {входВПрограмму.isPending
              ? 'Меняем…'
              : запись.cabinet_access
                ? 'Закрыть вход в программу'
                : 'Пустить в программу'}
          </Button.Root>
        )}
        {/* Фразу для входа задаёт ТОЛЬКО администратор: поставивший чужую фразу войдёт под
            этим человеком. У остальных кнопки нет вовсе — не серая, а именно нет: показанная
            кнопка сначала обещает, а потом отказывает. Вместо неё ниже написано, чем
            пользоваться. */}
        {можноМенять && яАдминистратор && !правимФразу && (
          <Button.Root
            type='button'
            variant='neutral'
            mode='stroke'
            size='xsmall'
            onClick={() => setПравимФразу(true)}
          >
            <Button.Icon as={RiKey2Line} />
            {запись.has_password ? 'Сменить фразу для программы' : 'Задать фразу для программы'}
          </Button.Root>
        )}
      </div>

      {отзыв.isError && <ErrorAlert error={отзыв.error} />}
      {отзыв.isSuccess && !запись.invite_live && (
        <Hint.Root>
          {отзыв.data.revoked > 0
            ? 'Прежняя ссылка больше не работает. Чтобы человек вошёл, выдайте новую.'
            : 'Гасить было нечего: живой ссылки не было. Если человек уже вошёл, ссылка его доступ не заберёт — закройте вход.'}
        </Hint.Root>
      )}
      {входВПрограмму.isError && <ErrorAlert error={входВПрограмму.error} />}

      {можноМенять && !яАдминистратор && (
        <Hint.Root>
          Фразу для входа в программу задаёт администратор: тот, кто ставит чужую фразу, может
          войти под этим человеком. В приложение на телефоне фраза не нужна — там вход по ссылке.
        </Hint.Root>
      )}

      {правимФразу && (
        <div className='flex flex-col gap-2 rounded-lg bg-bg-weak-50 px-3 py-3'>
          <TextField
            id={`фраза-${запись.id}`}
            label={`Новая фраза для входа «${запись.login}»`}
            value={новаяФраза}
            onChange={setНоваяФраза}
            type='text'
            autoComplete='off'
            maxLength={128}
            hint='Не короче 12 знаков. Продиктуйте её человеку и попросите сменить на свою: «Мой вход» под его именем внизу слева.'
          />
          {фраза.isError && <ErrorAlert error={фраза.error} />}
          <div className='flex flex-wrap gap-2'>
            <Button.Root
              type='button'
              variant='primary'
              mode='filled'
              size='xxsmall'
              onClick={() => {
                if (новаяФраза.length < 12) {
                  return;
                }
                фраза.mutate(новаяФраза);
              }}
            >
              {фраза.isPending ? 'Задаём…' : 'Задать фразу'}
            </Button.Root>
            <Button.Root
              type='button'
              variant='neutral'
              mode='ghost'
              size='xxsmall'
              onClick={() => {
                setПравимФразу(false);
                setНоваяФраза('');
                фраза.reset();
              }}
            >
              Отмена
            </Button.Root>
          </div>
          {новаяФраза !== '' && новаяФраза.length < 12 && (
            <Hint.Root hasError>
              Коротко: нужно не меньше 12 знаков, сейчас {новаяФраза.length}.
            </Hint.Root>
          )}
        </div>
      )}

      {выдаём && (
        <МастерВыдачи
          employee={employee}
          запись={запись}
          роли={роли}
          ролиЕдут={ролиЕдут}
          можноМенять={можноМенять}
          можноРоли={можноРоли}
          можноКоды={можноКоды}
          обновить={обновить}
          отмена={() => setВыдаём(false)}
        />
      )}

      {/* Телефон стоит НИЖЕ действий над входом: выдать ссылку — то, ради чего сюда заходят
          чаще всего, и кнопка не должна уезжать под блок на тридцать строк. Сюда же приходят
          с жалобой «не приходят уведомления»: список телефонов и проба в одном месте. */}
      <PhoneBlock userId={запись.id} />

      {/* Убрать запись целиком — по-прежнему в настройках, и это не забывчивость: у
          необратимого действия одно окно подтверждения и один вход в него. Сказано только
          тому, у кого право есть: остальным это лишняя строка. */}
      {можноУдалять && (
        <p className='text-paragraph-xs text-text-sub-600'>
          Убрать учётную запись целиком — в разделе «Настройки», там же, где список всех, кто
          входит в систему.
        </p>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════════════════
// Мастер: роль, потом ссылка
// ══════════════════════════════════════════════════════════════════════════════════════════

function МастерВыдачи({
  employee,
  запись,
  роли,
  ролиЕдут,
  можноМенять,
  можноРоли,
  можноКоды,
  обновить,
  отмена,
}: {
  employee: EmployeeOut;
  запись: UserOut | null;
  роли: RoleOut[];
  ролиЕдут: boolean;
  можноМенять: boolean;
  можноРоли: boolean;
  можноКоды: boolean;
  обновить: () => void;
  отмена: () => void;
}) {
  const [выбранные, setВыбранные] = useState<string[]>(запись?.roles ?? []);
  const [вПрограмму, setВПрограмму] = useState(запись?.cabinet_access ?? false);
  const [подсказка, setПодсказка] = useState<string | null>(null);
  const [итог, setИтог] = useState<ИтогВыдачи | null>(null);

  const выдача = useMutation({
    mutationFn: () =>
      выдатьДоступ(
        {
          employeeId: employee.id,
          фамилия: employee.display_name,
          запись,
        },
        {
          роли: выбранные,
          вПрограмму,
          можноРоли,
          можноЗаводить: можноМенять,
          // В карточке роль выбирают ЭТОМУ человеку, видя его нынешние отметки: смена —
          // осознанное действие, а не побочный эффект выдачи ссылки.
          менятьРольСуществующему: true,
        },
      ),
    onSuccess: (ответ) => {
      setИтог(ответ);
      обновить();
    },
  });

  const доступные = роли.filter((роль) => роль.assignable);
  const ролейНет = выбранные.length === 0;

  if (итог !== null) {
    return <ГотоваяСсылка итог={итог} закрыть={отмена} />;
  }

  return (
    <div className='flex flex-col gap-3 rounded-lg bg-bg-weak-50 px-3 py-3'>
      <span className='text-label-sm text-text-strong-950'>
        {запись === null ? 'Пригласить в систему' : 'Новая ссылка для входа'}
      </span>

      {/* Сначала РОЛЬ. Ссылка, выданная до роли, пускает человека туда, где ему не видно
          ничего, — и он читает это как поломку программы. */}
      {можноРоли ? (
        <div className='flex flex-col gap-2'>
          <span className='text-paragraph-sm text-text-sub-600'>
            Что человек будет видеть. Роли заменяются целиком: снятая пропадёт сразу.
          </span>
          {ролиЕдут ? (
            <Skeleton className='h-16 w-full' />
          ) : (
            <div className='grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2'>
              {доступные.map((роль) => (
                <CheckboxField
                  key={роль.code}
                  id={`доступ-роль-${роль.code}`}
                  label={роль.title}
                  checked={выбранные.includes(роль.code)}
                  onCheckedChange={() =>
                    setВыбранные((было) =>
                      было.includes(роль.code)
                        ? было.filter((код) => код !== роль.code)
                        : [...было, роль.code],
                    )
                  }
                />
              ))}
            </div>
          )}
          {/* Про недоступные сказано ОДИН раз и словами, а не девятью серыми строками. */}
          {!ролиЕдутИлиПусто(ролиЕдут, роли) && доступные.length < роли.length && (
            <span className='text-paragraph-xs text-text-sub-600'>
              Роли старше вашей здесь не перечислены: выдать их может финансовый или
              генеральный директор.
            </span>
          )}
        </div>
      ) : (
        <Hint.Root>
          Роль назначает администратор или финансовый директор. Вы можете выдать ссылку тому,
          у кого роль уже есть, — человек войдёт и увидит своё.
        </Hint.Root>
      )}

      {можноМенять && (
        <CheckboxField
          id='доступ-в-программу'
          label='Может входить в программу на компьютере'
          checked={вПрограмму}
          onCheckedChange={() => setВПрограмму((было) => !было)}
        />
      )}

      <div className='flex flex-wrap items-center gap-2'>
        <Button.Root
          type='button'
          variant='primary'
          mode='filled'
          size='xsmall'
          onClick={() => {
            if (выдача.isPending) {
              // Молчаливый выход по второму нажатию оставил бы человека без ответа, а второе
              // нажатие здесь дорого: оно завело бы вторую учётную запись.
              setПодсказка('Уже выдаём, ждём ответ сервера. Второй раз не отправляем.');
              return;
            }
            if (можноРоли && ролейНет) {
              setПодсказка(
                'Сначала выберите роль: без неё человек войдёт и не увидит ни своего расчётного листа, ни денег.',
              );
              return;
            }
            if (!можноКоды) {
              setПодсказка(
                'Ссылку для входа выдают администратор, финансовый директор, руководитель и операционный руководитель. Попросите кого-то из них.',
              );
              return;
            }
            setПодсказка(null);
            выдача.mutate();
          }}
        >
          <Button.Icon as={RiLinkM} />
          {выдача.isPending ? 'Выдаём…' : 'Выдать ссылку для входа'}
        </Button.Root>
        <Button.Root
          type='button'
          variant='neutral'
          mode='ghost'
          size='xxsmall'
          onClick={отмена}
        >
          Отмена
        </Button.Root>
      </div>

      {подсказка !== null && <Hint.Root hasError>{подсказка}</Hint.Root>}
      {выдача.isError && <ErrorAlert error={выдача.error} />}
    </div>
  );
}

/** Роли ещё едут либо их нет вовсе: тогда про «старше вашей» говорить нечего. */
function ролиЕдутИлиПусто(едут: boolean, роли: RoleOut[]): boolean {
  return едут || роли.length === 0;
}

/**
 * Готовая ссылка. Держится, пока человек не закроет её сам.
 *
 * 13.09.2026 владелец нажал «новый код» у нижней строки длинного списка и кода НЕ УВИДЕЛ: тот
 * нарисовался общей плашкой наверху, за пределами экрана. Здесь ответ появляется ровно там, где
 * нажимали, и никуда не уезжает: показать ссылку второй раз нельзя ни нам, ни серверу — в базе
 * лежит отпечаток.
 */
function ГотоваяСсылка({ итог, закрыть }: { итог: ИтогВыдачи; закрыть: () => void }) {
  if (итог.причина !== null) {
    return (
      <div className='flex flex-col gap-2 rounded-lg bg-bg-weak-50 px-3 py-3'>
        <span className='text-label-sm text-text-strong-950'>Ссылку выдать не вышло</span>
        <span className='max-w-prose text-paragraph-sm text-text-sub-600'>{итог.причина}</span>
        <div>
          <Button.Root
            type='button'
            variant='neutral'
            mode='ghost'
            size='xxsmall'
            onClick={закрыть}
          >
            Понятно
          </Button.Root>
        </div>
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-2 rounded-lg bg-bg-weak-50 px-3 py-3'>
      <span className='text-label-sm text-text-strong-950'>
        Ссылка готова. Код: <span className='tracking-widest'>{итог.код}</span>
      </span>
      <span className='max-w-prose text-paragraph-xs text-text-sub-600'>
        Пришлите ссылку: по ней человек войдёт одним нажатием, не набирая знаки. Годна трое
        суток{итог.годенДо === null ? '' : `, до ${formatDateOnly(итог.годенДо)}`}. Если ссылку
        выдавали раньше, прежняя больше не работает.
      </span>
      <div className='flex flex-wrap items-center gap-2'>
        <КнопкаКопирования
          что={итог.ссылка ?? ''}
          подпись='Скопировать ссылку'
          вид='filled'
        />
        <КнопкаКопирования что={итог.код ?? ''} подпись='Скопировать код' />
        <Button.Root
          type='button'
          variant='neutral'
          mode='ghost'
          size='xxsmall'
          onClick={закрыть}
        >
          Понятно
        </Button.Root>
      </div>
    </div>
  );
}
